import { EImportSectionsTarget } from '@constants';
import { ISection } from '@sdk/contracts';
import { Drawer } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SectionsList } from './components';
import { ContentWrapper, SelectorWrapper, Label, CustomAlert } from './ImportSectionDrawer.styles';
import { useImportSectionDrawer } from './useImportSectionDrawer';

import { CourseSelector } from '@/components/Form';
import { Icon } from '@/components/Icon';
import { ImportContentDrawerFooter } from '@/components/SectionManagement/ImportContentDrawerFooter';

const DRAWER_WIDTH = 747;

type ImportSectionDrawerProps = {
  target: EImportSectionsTarget;
  fetchOnlyParentsCourses?: boolean;
  isImporting?: boolean;
};

export const ImportSectionDrawer: React.FC<ImportSectionDrawerProps> = ({
  target,
  fetchOnlyParentsCourses,
  isImporting
}) => {
  const { t } = useTranslation(['ManagerBaseContentDetailTranslations', 'DefaultTranslations']);
  const { isOpen, closeDrawer, onImport } = useImportSectionDrawer();
  const [selectedCourseId, setSelectedCourseId] = useState<string>();
  const [selectedSections, setSelectedSections] = useState<ISection[]>([]);
  const [isSyncedToParent, setIsSyncedToParent] = useState(false);

  const handleClose = () => {
    if (isImporting) return;

    setSelectedCourseId(undefined);
    setSelectedSections([]);
    setIsSyncedToParent(false);
    closeDrawer();
  };

  const handleImport = () => {
    if (!selectedSections.length || !onImport) return;

    onImport({ sections: selectedSections, isSyncedToParent, onSuccess: handleClose });
  };

  const handleCourseChange = (courseId: string) => {
    setSelectedCourseId(courseId);
    setSelectedSections([]);
  };

  return (
    <Drawer
      open={isOpen}
      onClose={handleClose}
      destroyOnHidden
      width={DRAWER_WIDTH}
      title={t('IMPORT_SECTION')}
      footer={
        <ImportContentDrawerFooter
          isLoading={isImporting}
          isSyncedToParent={isSyncedToParent}
          onSyncChange={setIsSyncedToParent}
          onCancel={handleClose}
          onImport={handleImport}
          isImportDisabled={!selectedSections.length}
          showSyncCheckbox={fetchOnlyParentsCourses}
        />
      }>
      <ContentWrapper>
        <CustomAlert
          message={t('ManagerBaseContentDetailTranslations:IMPORT_SECTION_ALERT')}
          type="info"
          showIcon
          icon={<Icon i="InformationFilled" size={18} />}
        />
        <SelectorWrapper>
          <Label>{t('ManagerBaseContentDetailTranslations:COURSE_SELECTOR_LABEL')}</Label>
          <CourseSelector
            filters={
              fetchOnlyParentsCourses !== undefined
                ? {
                    isParent: fetchOnlyParentsCourses
                  }
                : undefined
            }
            value={selectedCourseId}
            onChange={handleCourseChange}
            styles={{ width: '100%' }}
          />
        </SelectorWrapper>
        <SectionsList
          courseId={selectedCourseId}
          selectedSections={selectedSections}
          onSelectionChange={setSelectedSections}
          target={target}
        />
      </ContentWrapper>
    </Drawer>
  );
};
